import React, {
    KeyboardEvent,
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { TextareaAutosize } from "@mui/material";
import axiosInstant from "../../api";
import {
    TaggableUser,
    activeMentionQuery,
    applyMention,
    filterTaggable,
} from "./MentionText";

interface Props {
    value: string;
    onChange: (value: string) => void;
    usersUrl: string | null;
    onMentionPicked: (user: TaggableUser) => void;
    placeholder?: string;
    className?: string;
    minRows?: number;
    maxRows?: number;
    maxLength?: number;
    ariaLabel?: string;
    disabled?: boolean;
    onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
}

export const MentionTextarea = forwardRef<HTMLTextAreaElement, Props>(({
    value,
    onChange,
    usersUrl,
    onMentionPicked,
    placeholder,
    className,
    minRows = 3,
    maxRows = 5,
    maxLength,
    ariaLabel,
    disabled,
    onKeyDown: onKeyDownProp,
}, forwardedRef) => {
    const ref = useRef<HTMLTextAreaElement>(null);
    useImperativeHandle(forwardedRef, () => ref.current as HTMLTextAreaElement);
    const [users, setUsers] = useState<TaggableUser[]>([]);
    const [wanted, setWanted] = useState(false);
    const [caret, setCaret] = useState(0);
    const [open, setOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(0);

    useEffect(() => {
        let live = true;
        if (!usersUrl || !wanted) {
            setUsers([]);
            return undefined;
        }
        axiosInstant
            .get(usersUrl)
            .then(({ data }) => live && setUsers(Array.isArray(data) ? data : []))
            .catch(() => live && setUsers([]));
        return () => {
            live = false;
        };
    }, [usersUrl, wanted]);

    const active = useMemo(
        () => (open && users.length ? activeMentionQuery(value, caret) : null),
        [open, users.length, value, caret],
    );
    const matches = useMemo(
        () => (active ? filterTaggable(users, active.query) : []),
        [active, users],
    );
    const isOpen = !!active && matches.length > 0;

    const pick = (user: TaggableUser) => {
        if (!active) return;
        const next = applyMention(value, caret, active.start, user);
        onChange(next.value);
        onMentionPicked(user);
        setOpen(false);
        setHighlighted(0);
        requestAnimationFrame(() => {
            ref.current?.focus();
            ref.current?.setSelectionRange(next.caret, next.caret);
            setCaret(next.caret);
        });
    };

    const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (!isOpen) {
            onKeyDownProp?.(event);
            return;
        }
        if (event.key === "ArrowDown") {
            event.preventDefault();
            setHighlighted((i) => (i + 1) % matches.length);
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setHighlighted((i) => (i - 1 + matches.length) % matches.length);
        } else if (event.key === "Enter" || event.key === "Tab") {
            event.preventDefault();
            pick(matches[Math.min(highlighted, matches.length - 1)]);
        } else if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            setOpen(false);
        } else {
            onKeyDownProp?.(event);
        }
    };

    const syncCaret = () => setCaret(ref.current?.selectionStart ?? 0);

    return (
        <div className="relative">
            <TextareaAutosize
                ref={ref}
                minRows={minRows}
                maxRows={maxRows}
                value={value}
                maxLength={maxLength}
                aria-label={ariaLabel}
                disabled={disabled}
                placeholder={placeholder}
                className={className}
                onChange={(e) => {
                    onChange(e.target.value);
                    setCaret(e.target.selectionStart ?? 0);
                    if (e.target.value.includes("@")) setWanted(true);
                    setOpen(true);
                    setHighlighted(0);
                }}
                onKeyDown={onKeyDown}
                onKeyUp={syncCaret}
                onClick={syncCaret}
                onBlur={() => setTimeout(() => setOpen(false), 120)}
            />
            {isOpen && (
                <ul
                    role="listbox"
                    aria-label="Tag someone"
                    className="absolute z-20 left-0 bottom-full mb-1 w-full max-w-[320px] max-h-[220px] overflow-y-auto bg-surface border border-border-token rounded-xl shadow-[var(--sh-2)] py-1 m-0 list-none"
                >
                    {matches.map((user, index) => (
                        <li key={user.userid}>
                            <button
                                type="button"
                                role="option"
                                aria-selected={index === highlighted}
                                onMouseDown={(e) => e.preventDefault()}
                                onMouseEnter={() => setHighlighted(index)}
                                onClick={() => pick(user)}
                                className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 cursor-pointer border-0 bg-transparent ${
                                    index === highlighted ? "bg-surface-2" : ""
                                }`}
                            >
                                <span className="text-[13.5px] text-text-1 truncate">
                                    {user.displayName}
                                </span>
                                {!user.internal && (
                                    <span className="shrink-0 text-[11px] text-text-2">Brand</span>
                                )}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
});

MentionTextarea.displayName = "MentionTextarea";
